<template>
    <ModalShell :title="title" :subtitle="description" width="sm:max-w-[560px] sm:w-full w-11/12">
        <template #icon>
            <PhWarningCircle :size="24" weight="regular" class="text-zinc-700" />
        </template>
        <template #header-action>
            <button type="button" class="close-btn" aria-label="Close confirmation" @click="closeDialog">
                <PhX :size="20" />
            </button>
        </template>

        <!-- Focusable without joining the tab order: where focus lands when the entry the member
             pressed on takes its own remove button with it. -->
        <div ref="bodyEl" tabindex="-1" class="confirmation-body">
            <ul v-if="features.length" ref="featureList" class="feature-list">
                <li v-for="(modalFeature, featureIndex) in features" :key="modalFeature.key">
                    <div class="feature-entry">
                        <div class="feature-text">
                            <strong>{{ modalFeature.label }}</strong>
                            <span v-if="modalFeature.description">{{ modalFeature.description }}</span>
                        </div>
                        <!-- Narrowing the set happens here rather than by leaving the dialog, so
                             choosing a subset costs no more than accepting all of it. The opener
                             owns the set and narrows it; this dialog reads the result live. -->
                        <button
                            v-if="removable"
                            type="button"
                            class="feature-remove"
                            :aria-label="removeLabel(modalFeature.label)"
                            @click="removeFeature(modalFeature.key, featureIndex)"
                        >
                            <PhX :size="16" />
                        </button>
                    </div>
                </li>
            </ul>
            <p v-if="warning" class="modal-warning">
                {{ warning }}
            </p>
        </div>

        <template #footer>
            <PrimeButton class="p-btn-root_secondary" :label="cancelLabel" @click="closeDialog" />
            <PrimeButton :label="content.confirmLabel" @click="confirmAction" />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import { PhWarningCircle, PhX } from '@phosphor-icons/vue';
import { computed, nextTick, ref, unref } from 'vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.CONFIRMATION);

// `unref`, never `.value`: PrimeVue's reactive dialog instance unwraps the opener's ref on read —
// see ReactiveModalData in types/modals.ts. Either way this stays live as the opener narrows.
const content = computed(() => unref(dialogRef.value.data.content));

const features = computed(() => content.value.features ?? []);
const removable = computed(() => content.value.removable ?? false);
const cancelLabel = computed(() => content.value.cancelLabel ?? 'Cancel');
const removeLabel = computed(() => dialogRef.value.data.removeLabel ?? ((label: string) => `Remove ${label}`));

const title = computed(() => content.value.title || 'Permanent Action Required');

const description = computed(() => {
    if (content.value.description) return content.value.description;
    if (features.value.length === 1) {
        return `You are about to enable ${features.value[0].label}. After Privacy Bee submits the related remediation request on your behalf, the action is permanent and Privacy Bee cannot reverse it.`;
    }
    return 'This action enables settings that begin permanent remediation work.';
});

// `??`, not `||`: a caller that passes an empty string is saying this dialog needs no warning
// line, which is different from not having said anything about one.
const warning = computed(() => content.value.warning ?? 'Continue only if this matches your intent.');

const bodyEl = ref<HTMLElement | null>(null);
const featureList = ref<HTMLElement | null>(null);

const closeDialog = () => {
    dialogRef.value.close();
};

/**
 * Drop one entry, then put focus somewhere deterministic inside the dialog.
 *
 * The button the member pressed unmounts with its entry, and at two entries every remove button
 * unmounts at once because a set of one can no longer be narrowed. Either way the browser would
 * drop focus to the page body while a consent dialog is still open, leaving Tab as the only way
 * back in and nothing announced to say what happened.
 */
const removeFeature = async (featureKey: string, featureIndex: number) => {
    dialogRef.value.data.onRemove?.(featureKey);
    await nextTick();

    const removeButtons = Array.from(featureList.value?.querySelectorAll<HTMLElement>('.feature-remove') ?? []);
    const nextButton = removeButtons[Math.min(featureIndex, removeButtons.length - 1)];

    (nextButton ?? bodyEl.value)?.focus();
};

// The one confirmation: every other way out of this dialog — Cancel, the close control, Escape —
// closes with no payload, and the opener reads that as a member who declined.
const confirmAction = () => {
    dialogRef.value.close({ confirmed: true });
};
</script>

<style lang="scss" scoped>
.close-btn {
    @apply flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700;
}

.confirmation-body {
    @apply w-full;

    // Focus is placed here only so the dialog keeps its place after an entry unmounts; the ring
    // would read as a control the member is expected to act on. A keyboard arrival is a different
    // event and keeps the floor's ring.
    &:focus:not(:focus-visible) {
        @apply outline-none;
    }
}

.feature-list {
    @apply mt-2 flex w-full flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3;

    li {
        @apply flex flex-col gap-1 text-sm leading-5 text-zinc-700;
    }

    .feature-entry {
        @apply flex items-start justify-between gap-3;
    }

    .feature-text {
        @apply flex flex-col gap-1;
    }

    .feature-remove {
        @apply rounded px-1 text-zinc-500 transition-colors hover:text-zinc-700;
    }

    strong {
        @apply text-zinc-950;
    }

    span {
        @apply text-xs leading-5 text-zinc-600;
    }
}

.modal-warning {
    @apply mb-0 mt-4 text-sm font-semibold text-blue-700;
}
</style>
